import React, { useEffect, useState } from 'react';
import { Typography, Box, Tooltip, makeStyles, IconButton, Fade } from '@material-ui/core';
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

// Custom styles for the InfoBox component
const useStyles = makeStyles((theme) => ({
  infoContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
    position: 'relative',
    transition: 'all 0.3s ease',
    background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
    borderRadius: 20,
    padding: '4px 12px',
    boxShadow: '0 3px 5px rgba(0, 0, 0, 0.12)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    maxHeight: 28,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 5px 8px rgba(0, 0, 0, 0.15)'
    }
  },
  infoButton: {
    padding: 0,
    minWidth: 20,
    height: 20,
    color: '#fff',
    //marginRight: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)'
    }
  },
  infoIcon: {
    fontSize: '0.9rem',
    transition: 'transform 0.2s ease',
    color: '#fff',
    '&:hover': {
      transform: 'scale(1.1)'
    }
  },
  infoText: {
    fontSize: '0.7rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    color: '#fff',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    textTransform: 'uppercase'
  },
  infoLink: {
    color: '#fff',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      textDecoration: 'none',
      color: 'rgba(255, 255, 255, 0.9)'
    }
  },
  tooltip: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[4],
    borderRadius: 12,
    padding: theme.spacing(1.5),
    fontSize: '0.75rem',
    maxWidth: 250,
    border: `1px solid ${theme.palette.divider}`
  },
  pulse: {
    animation: '$pulse 2s infinite'
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(63, 81, 181, 0.4)'
    },
    '70%': {
      boxShadow: '0 0 0 6px rgba(63, 81, 181, 0)'
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(63, 81, 181, 0)'
    }
  }
}));

const InfoBox = ({ name, tooltipText }) => {
  const classes = useStyles();
  const [informationLink, setInformationLink] = useState('');
  const [infoTitle, setInfoTitle] = useState('More Info');

  // Retrieve information link from local storage based on the provided name
  useEffect(() => {
    const infoLinkData = JSON.parse(localStorage.getItem('infoLinkData'));
    if (infoLinkData && infoLinkData[name]) {
      setInformationLink(infoLinkData[name].infoLink || name);
      setInfoTitle(infoLinkData[name].title || 'More Info');
    } else {
      setInformationLink(name);
    }
  }, [name]);

  const tooltipContent = tooltipText || 'Click for more info.';

  return (
    <>
      {informationLink && (
        <Tooltip 
          title={tooltipContent}
          placement="top"
          arrow
          TransitionComponent={Fade}
          TransitionProps={{ timeout: 600 }}
          classes={{ tooltip: classes.tooltip }}
        >
          <Box className={`${classes.infoContainer} ${classes.pulse}`} component="a" 
            href={informationLink}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <IconButton 
              className={classes.infoButton} 
              aria-label="information"
              disableRipple
              size="small"
            >
              <InfoOutlinedIcon className={classes.infoIcon} />
            </IconButton>
            <Typography variant="caption" className={classes.infoText}>
              {infoTitle}
            </Typography>
          </Box>
        </Tooltip>
      )}
    </>
  );
};

export default InfoBox;
